import { NextPageContext } from "next";
import { NextPageContextEnhanced, TypeLogLevel } from "../types/page";
import { applySLR } from "./enhancers/applySLR";
import { applyHostDomain } from "./enhancers/1_applyHostDomain";
import { applyCookies } from "./enhancers/2_applyCookies";
import { applyCountryCode } from "./enhancers/8_applyCountryCode";
import { applyCacheManager } from "./enhancers/10_applyCacheManager";
import { applyEssentialData } from "./enhancers/11_applyEssentialData";
import { applyCountryObject } from "./enhancers/12_applyCountryObject";
import { Enum410Reason } from "../libs/router/types/types";
import axios from "axios";
import {initSeoData} from "../libs/seo-widget/helpers/initSeoData";

export const doRedirect = (
  context: NextPageContextEnhanced,
  urlTo: string,
  statusCode: 301 | 302 | 307 | 308,
  trace_message: string = "unknown",
  isGetServerSideProps: boolean = false
) => {
  if (context.isRedirectDone) {
    return;
  }
  context.isRedirectDone = true;
  let newStatusCode = process.env.PROFILE_ENV != "localhost" ? statusCode : 302; // on local machine always go to 302

  console.log("-> doRedirect trace:", {
    reqUri: context.requestUri,
    statusCode,
    redirectTo: urlTo,
    trace_message,
  });

  if (true === isGetServerSideProps) {
    // in case doRedirect call done from getServerSideProps(), we return "notFound" : true to stay
    // compatible with next.js documentation
    return {
      redirect: {
        //permanent: true,
        destination: encodeURI(decodeURI(urlTo)),
        statusCode,
      },
    };
  } else {
    try {
      context.res.writeHead(newStatusCode, {
        Location: urlTo,
        "X-redirect-status-code": statusCode,
        "X-redirect-trace": trace_message,
      });
    } catch (e) {
      context.res.writeHead(newStatusCode, {
        Location: encodeURI(urlTo),
        "X-redirect-status-code": statusCode,
        "X-redirect-trace": trace_message,
      });
    }
    //res.end() does not equal to php exit() function
    //this means execution will continue to next line
    context.res.end(); //flush headers
  }
};

export const error410 = (
  context: NextPageContextEnhanced,
  reason: Enum410Reason,
  isGetServerSideProps: boolean = false
): any => {
  // @ts-ignore
  context.res.status(410);
  // @ts-ignore
  context.req.displayError(null, 410, false);
  console.log("-> error410 trace:", {
    reqUri: context.requestUri,
    reason,
  });
  // @ts-ignore
  if (isGetServerSideProps) {
    return {
      props: { is410: true },
    };
  } else {
    context.res.end();
  }
};

export default async function enhanceContext(
  ctx: NextPageContext
): Promise<NextPageContextEnhanced> {
  // @ts-ignore
  const context: NextPageContextEnhanced = ctx;

  if (context.isEnhanced) {
    return context;
  }
  context.isEnhanced = true;

  context.setData = function (key, value) {
    context.req.customContext.requestDataBag[key] = value;
  };
  context.getData = function (key) {
    return context.req.customContext.requestDataBag[key];
  };

  context.requireAuth = () => {
    if (context.memberData?.id) return null;
    return () =>
      doRedirect(
        context,
        `/${context.lang}?openSLR=true&returnUrl=${context.requestUri}`,
        302,
        "login-redirect",
        true
      );
  };

  context.initSeoData = initSeoData.bind(null, context);

  context.techLogger = (level: TypeLogLevel, label: string, payload?: any) => {
    const msg = payload ? JSON.stringify(payload) : "";
    const category = `[server]${label}`;
    const data = { level, msg, category };
    console.log("techLogger:errorLabel:", data);
    return axios
      .post(`${process.env.API_URL}/logging/tech-log`, data, {
        headers: {
          "content-type": "application/json",
          source: context.getData("source") ?? "desktop",
          country: context.countryCode ?? "",
        },
      })
      .then((response) => {
        return response.data;
      })
      .catch((e) => {
        console.log("techLogger:", e.message);
      });
  };
  applyHostDomain(context);

  applyCookies(context);

  applyCountryCode(context);

  applyCacheManager(context);

  applyEssentialData(context);

  // any api request applied should have locale/lang available inside the context
  applyCountryObject(context);

  if (context.isRequestClosed) {
    return context;
  }

  await applySLR(context, context.cookies);


  return context as NextPageContextEnhanced;
}
